/**
 * Framework Coverage Calculator
 *
 * Computes per-framework coverage percentages by analyzing which maturity
 * assessment questions map to each framework and how the client scored.
 * This measures assessment intersection with framework requirements,
 * NOT compliance certification.
 */

import maturityAssessmentData from '../data/maturityAssessment.json'

const FRAMEWORK_NORMALIZATION = {
  'NIST CSF': 'NIST CSF',
  'NIST': 'NIST CSF',
  'CIS CSC': 'CIS Controls',
  'CIS Controls': 'CIS Controls',
  'CIS': 'CIS Controls',
  'ISO/IEC 27001': 'ISO 27001',
  'ISO 27001': 'ISO 27001',
  'ISO 27001:2022': 'ISO 27001',
  'ISO/IEC 27001:2022': 'ISO 27001',
  'ISO/IEC 27002': 'ISO 27002',
  'ISO/IEC 27004': 'ISO 27004',
  'ISO/IEC 27017': 'ISO 27017',
  'ISO/IEC 27035': 'ISO 27035',
  'ISO/IEC 27036': 'ISO 27036',
  'ISO/IEC 27701': 'ISO 27701',
  'ISO/IEC 42001': 'ISO 42001',
  'ISO 22301': 'ISO 22301',
  'HIPAA': 'HIPAA',
  'HIPAA Security Rule': 'HIPAA',
  'PCI DSS': 'PCI DSS',
  'PCI-DSS': 'PCI DSS',
  'SOC 2': 'SOC 2',
  'SOX': 'SOX',
  'GDPR': 'GDPR',
  'CCPA': 'CCPA',
  'CMMC': 'CMMC',
  'GLBA': 'GLBA',
  'FERPA': 'FERPA',
  'NERC CIP': 'NERC CIP',
  'FedRAMP': 'FedRAMP',
  'NYDFS': 'NYDFS',
  'FFIEC': 'FFIEC',
  'NIST AI RMF': 'NIST AI RMF',
  'EU AI Act': 'EU AI Act'
}

/**
 * Parse a frameworkMapping string into normalized framework identifiers.
 * e.g., "NIST CSF PR.AC-1; CIS CSC 6; ISO/IEC 27001 A.9"
 * → ['NIST CSF', 'CIS Controls', 'ISO 27001']
 */
export function parseFrameworkMapping(mappingString) {
  if (!mappingString) return []

  const refs = mappingString.split(';').map(s => s.trim()).filter(Boolean)
  const frameworks = new Set()

  for (const ref of refs) {
    // Try matching against known framework prefixes (longest match first)
    const sortedKeys = Object.keys(FRAMEWORK_NORMALIZATION).sort((a, b) => b.length - a.length)
    let matched = false

    for (const prefix of sortedKeys) {
      if (ref.startsWith(prefix)) {
        frameworks.add(FRAMEWORK_NORMALIZATION[prefix])
        matched = true
        break
      }
    }

    // Fallback: extract the alphabetic prefix before any control reference
    if (!matched) {
      const cleanName = ref
        .replace(/\s+§\S+.*/g, '')
        .replace(/\s+Arts?\.\S+.*/g, '')
        .replace(/\s+Chapter\s+\S+.*/g, '')
        .replace(/\s+\(conceptual\).*/g, '')
        .replace(/\s+\d+[\.\-]\S+.*/g, '')
        .replace(/\s+[A-Z]{1,3}[\.\-]\S+.*/g, '')
        .trim()

      if (cleanName && FRAMEWORK_NORMALIZATION[cleanName]) {
        frameworks.add(FRAMEWORK_NORMALIZATION[cleanName])
      }
    }
  }

  return [...frameworks]
}

/**
 * Build question-to-framework mapping index.
 * Returns { frameworkName: [{ questionId, category, question, frameworkMapping }] }
 */
function buildFrameworkQuestionIndex() {
  const index = {}

  maturityAssessmentData.sections.forEach(section => {
    section.questions.forEach(q => {
      if (!q.frameworkMapping) return
      const frameworks = parseFrameworkMapping(q.frameworkMapping)
      frameworks.forEach(fw => {
        if (!index[fw]) index[fw] = []
        index[fw].push({
          questionId: q.id,
          category: q.category,
          question: q.question,
          frameworkMapping: q.frameworkMapping
        })
      })
    })
  })

  return index
}

/**
 * Build the complete framework coverage analysis.
 *
 * @param {Object} maturityScores - From calculateScores(), contains maturity.categoryScores
 * @param {Object} maturityResponses - Raw maturity assessment responses { 'ma-0': 'mostly', ... }
 * @param {Array} applicableFrameworks - Framework names applicable to this client
 * @returns {Object} Coverage analysis per framework
 */
export function calculateFrameworkCoverage(maturityScores, maturityResponses, applicableFrameworks = []) {
  const questionIndex = buildFrameworkQuestionIndex()
  const responses = maturityResponses || {}

  const SCORE_MAP = { 'no': 0, 'partially': 1, 'mostly': 2, 'yes': 3, 'unknown': 0 }
  const MAX_SCORE = 3

  const frameworks = {}
  if (!applicableFrameworks || applicableFrameworks.length === 0) {
    return { frameworks: {}, summary: { frameworkCount: 0, bestCoveredFramework: null, leastCoveredFramework: null, averageCoverageScore: 0, assessmentCoverageDisclaimer: 'No applicable frameworks specified for coverage analysis.' } }
  }

  const applicableSet = new Set(applicableFrameworks.map(f => FRAMEWORK_NORMALIZATION[f] || f))
  const frameworksToAnalyze = Object.keys(questionIndex).filter(fw => applicableSet.has(fw))

  for (const fw of frameworksToAnalyze) {
    const questions = questionIndex[fw] || []
    if (questions.length === 0) continue

    let totalScore = 0
    let scoredCount = 0
    let naCount = 0
    const domainScores = {}

    questions.forEach(q => {
      const response = responses[q.questionId]
      if (response === 'na' || response === null || response === undefined) {
        naCount++
        return
      }

      const score = SCORE_MAP[response]
      if (score !== undefined) {
        totalScore += score
        scoredCount++

        if (!domainScores[q.category]) domainScores[q.category] = { total: 0, count: 0 }
        domainScores[q.category].total += score
        domainScores[q.category].count++
      }
    })

    const averageScore = scoredCount > 0 ? Math.round((totalScore / (scoredCount * MAX_SCORE)) * 100) : 0

    // Determine strong and weak domains
    const domainResults = Object.entries(domainScores).map(([name, data]) => ({
      name,
      score: Math.round((data.total / (data.count * MAX_SCORE)) * 100)
    })).sort((a, b) => b.score - a.score)

    const strongDomains = domainResults.filter(d => d.score >= 60).map(d => d.name)
    const weakDomains = domainResults.filter(d => d.score < 40).map(d => d.name)

    frameworks[fw] = {
      frameworkName: fw,
      questionCount: questions.length,
      answeredCount: scoredCount,
      naCount,
      averageScore,
      strongDomains,
      weakDomains,
      coverageNote: `Our assessment evaluates ${questions.length} controls that intersect with ${fw} requirements.${naCount > 0 ? ` ${naCount} questions marked N/A.` : ''} This is not a full ${fw} assessment.`,
      questionIds: questions.map(q => q.questionId)
    }
  }

  // Summary
  const frameworkEntries = Object.values(frameworks)
  const bestCovered = frameworkEntries.length > 0 ? frameworkEntries.reduce((a, b) => a.averageScore > b.averageScore ? a : b) : null
  const leastCovered = frameworkEntries.length > 0 ? frameworkEntries.reduce((a, b) => a.averageScore < b.averageScore ? a : b) : null
  const avgScore = frameworkEntries.length > 0 ? Math.round(frameworkEntries.reduce((sum, f) => sum + f.averageScore, 0) / frameworkEntries.length) : 0

  return {
    frameworks,
    summary: {
      frameworkCount: frameworkEntries.length,
      bestCoveredFramework: bestCovered?.frameworkName || null,
      leastCoveredFramework: leastCovered?.frameworkName || null,
      averageCoverageScore: avgScore,
      assessmentCoverageDisclaimer: 'Coverage percentages reflect maturity in assessment areas that intersect with each framework. A formal compliance assessment against these frameworks should be conducted independently.'
    }
  }
}
